import {
  Layers, Sparkles, Move3D, Tag, CheckSquare, MessageSquare,
  Filter, ShieldCheck, Smartphone, Zap, GanttChartSquare, Bell,
  LayoutTemplate, Paperclip,
} from "lucide-react";

const FEATURES = [
  {
    icon: LayoutTemplate,
    title: "Plantillas listas para usar",
    desc: "Empieza con tableros pre-cargados: mudanza, fiesta, vacaciones, negocio, marketing, producto o ventas. Personaliza y lánzate en segundos.",
    accent: "violet",
  },
  {
    icon: Paperclip,
    title: "Adjuntos con preview",
    desc: "Sube imágenes y archivos a cualquier tarjeta — preview inline para fotos y descarga directa para documentos.",
    accent: "blue",
  },
  {
    icon: Move3D,
    title: "Drag & drop fluido",
    desc: "Mueve tarjetas entre listas con mouse o touch. Reordenamiento en tiempo real, persistencia instantánea.",
    accent: "blue",
  },
  {
    icon: GanttChartSquare,
    title: "Vista Sprints",
    desc: "Programa tareas con fechas de inicio y fin. Arrastra las barras para reprogramar. La misma data del board, ahora en línea de tiempo.",
    accent: "violet",
  },
  {
    icon: Tag,
    title: "Etiquetas a tu medida",
    desc: "Crea etiquetas con color propio para clasificar por proyecto, área o estado. Filtra al instante.",
    accent: "rose",
  },
  {
    icon: CheckSquare,
    title: "Checklists con progreso",
    desc: "Convierte tareas grandes en sub-tareas. La barra de progreso te dice exactamente cuánto falta.",
    accent: "emerald",
  },
  {
    icon: MessageSquare,
    title: "Comentarios con Markdown",
    desc: "Negritas, listas, links y código. Conversa con tu equipo sin salir de la tarjeta.",
    accent: "sky",
  },
  {
    icon: Filter,
    title: "Búsqueda y filtros potentes",
    desc: "Filtra por etiqueta, miembro, prioridad o fecha límite. Encuentra cualquier tarea en segundos.",
    accent: "amber",
  },
  {
    icon: Layers,
    title: "Workspaces ilimitados",
    desc: "Separa proyectos personales del trabajo de equipo. Cada workspace con sus propios boards y miembros.",
    accent: "blue",
  },
  {
    icon: Bell,
    title: "Actividad reciente",
    desc: "Feed de cambios por tablero. Quién movió qué, cuándo y por qué — sin perder contexto.",
    accent: "violet",
  },
  {
    icon: Smartphone,
    title: "100% responsive",
    desc: "Diseñado para desktop, tablet y mobile. Mismo poder, mismo flujo, en cualquier pantalla.",
    accent: "emerald",
  },
  {
    icon: ShieldCheck,
    title: "Seguro por defecto",
    desc: "Conexiones HTTPS, sesiones HttpOnly, autenticación con Google. Tus datos privados, siempre.",
    accent: "sky",
  },
  {
    icon: Sparkles,
    title: "Diseñado para enfocarte",
    desc: "Interfaz minimalista con modo claro/oscuro. Sin distracciones, solo tu trabajo.",
    accent: "rose",
  },
  {
    icon: Zap,
    title: "Rápido siempre",
    desc: "Optimistic updates y persistencia en background. La app responde antes de que termines de hacer click.",
    accent: "amber",
  },
];

const ACCENT = {
  blue:    "bg-blue-500/10 text-blue-600 dark:text-blue-300 ring-blue-500/20",
  violet:  "bg-violet-500/10 text-violet-600 dark:text-violet-300 ring-violet-500/20",
  rose:    "bg-rose-500/10 text-rose-600 dark:text-rose-300 ring-rose-500/20",
  emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 ring-emerald-500/20",
  sky:     "bg-sky-500/10 text-sky-600 dark:text-sky-300 ring-sky-500/20",
  amber:   "bg-amber-500/10 text-amber-600 dark:text-amber-300 ring-amber-500/20",
};

export default function FeaturesSection() {
  return (
    <section id="features" className="relative py-20 md:py-28" data-testid="landing-features">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl">
          <span className="inline-block text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
            La herramienta más completa
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-2 leading-[1.05]">
            Todo lo que necesitas para administrar proyectos sin caos.
          </h2>
          <p className="text-muted-foreground mt-4 text-base md:text-lg">
            12 capacidades pensadas para que tú y tu equipo trabajen rápido, organizados,
            y siempre alineados. Sumadas a las <b>3 vistas</b> (Rolls, Sprints y Matriz),
            tienes la combinación más completa del mercado.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
          {FEATURES.map(({ icon: Icon, title, desc, accent }, i) => (
            <div
              key={title}
              data-testid={`feature-card-${i}`}
              className="glass-card rounded-2xl p-6 hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-1 transition will-change-transform"
              style={{ animation: "fade-rise 0.6s ease-out both", animationDelay: `${i * 40}ms` }}
            >
              <div className={`h-11 w-11 rounded-xl grid place-items-center ring-1 ${ACCENT[accent]}`}>
                <Icon className="h-5 w-5" />
              </div>
              <p className="font-display font-semibold text-lg mt-4 leading-tight">{title}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
